<script>
import { algoliaGlobalSearch, GLOBAL_SEARCH_INDEXES } from '../api/algolia';
import { search as searchUsers } from '../api/users';
import queryString from '../lib/query-string';

export default {
  directives: {
    margin: require('../directives/margins').margin,
    marginTop: require('../directives/margins').marginTop,
    marginRight: require('../directives/margins').marginRight,
    marginBottom: require('../directives/margins').marginBottom,
    marginLeft: require('../directives/margins').marginLeft,
  },
  components: {
    VueSimpleSpinner: require('../vue-simple-spinner').default,
    InfiniteLoading: require('vue-infinite-loading').default,
  },
  data() {
    return {
      firstTime: true,
      error: null,
      pageSize: 3,
      query: queryString.parse(location.search).q,
      // Algolia pages starts at 0 index
      crowdpacs: {
        results: [],
        page: 0,
        totalResults: 0,
        totalPages: 0,
      },
      candidates: {
        results: [],
        page: 0,
        totalResults: 0,
        totalPages: 0,
      },
      petitions: {
        results: [],
        page: 0,
        totalResults: 0,
        totalPages: 0,
      },
      news: {
        results: [],
        page: 0,
        totalResults: 0,
        totalPages: 0,
      },
      // Laravel pages starts at 1 index
      users: {
        results: [],
        page: 1,
        totalResults: 0,
        totalPages: 0,
      },
      records: [],
    };
  },
  computed: {
    noMoreResults() {
      return (
        !this.firstTime &&
        this.crowdpacs.page == this.crowdpacs.totalPages &&
        this.candidates.page == this.candidates.totalPages &&
        this.users.page == this.users.totalPages &&
        this.petitions.page == this.petitions.totalPages &&
        this.news.page == this.news.totalPages
      );
    },
  },
  methods: {
    async fetch($state) {
      if (this.noMoreResults) {
        $state.complete();
      } else {
        const query = this.query?.trim() || '';
        this.firstTime = false;

        if (query === '') {
          $state.complete();
        } else {
          try {
            let finished = true;
            this.error = null;

            const response = await algoliaGlobalSearch({
              query: query,
              pageSize: this.pageSize,
              crowdpacs: this.crowdpacs,
              candidates: this.candidates,
              petitions: this.petitions,
              news: this.news,
            });

            const crowdpacsData = response.results[GLOBAL_SEARCH_INDEXES.crowdpacs];
            const candidatesData = response.results[GLOBAL_SEARCH_INDEXES.candidates];
            const petitionsData = response.results[GLOBAL_SEARCH_INDEXES.petitions];
            const newsData = response.results[GLOBAL_SEARCH_INDEXES.news];

            this.crowdpacs.results = crowdpacsData.hits;
            this.crowdpacs.totalResults = crowdpacsData.nbHits;
            this.crowdpacs.totalPages = crowdpacsData.nbPages;

            if (this.crowdpacs.totalPages > this.crowdpacs.page) {
              finished = false;
              this.crowdpacs.page += 1;
            }

            this.candidates.results = candidatesData.hits;
            this.candidates.totalResults = candidatesData.nbHits;
            this.candidates.totalPages = candidatesData.nbPages;

            if (this.candidates.totalPages > this.candidates.page) {
              finished = false;
              this.candidates.page += 1;
            }

            this.petitions.results = petitionsData.hits;
            this.petitions.totalResults = petitionsData.nbHits;
            this.petitions.totalPages = petitionsData.nbPages;

            if (this.petitions.totalPages > this.petitions.page) {
              finished = false;
              this.petitions.page += 1;
            }

            this.news.results = newsData.hits;
            this.news.totalResults = newsData.nbHits;
            this.news.totalPages = newsData.nbPages;

            if (this.news.totalPages > this.news.page) {
              finished = false;
              this.news.page += 1;
            }

            const usersData = await searchUsers({
              query: query,
              page: this.users.page,
            });

            this.users.results = usersData.hits;
            this.users.totalResults = usersData.total;
            this.users.totalPages = usersData.pages;

            if (this.users.totalPages > this.users.page) {
              finished = false;
              this.users.page += 1;
            }

            // The order here is very important! Do not change it
            this.records = this.records.concat(this.candidates.results);
            this.records = this.records.concat(this.crowdpacs.results);
            this.records = this.records.concat(this.users.results);
            this.records = this.records.concat(this.news.results);
            this.records = this.records.concat(this.petitions.results);

            this.query = query;

            if (finished) {
              if (this.records.length > 0) {
                $state.loaded();
              } else {
                $state.complete();
              }
            } else {
              $state.loaded();
            }
          } catch (error) {
            this.error = error;
            $state.complete();
          }
        }
      }
    },
    getRecordTitle(record) {
      switch (record.objectType) {
        case 'crowdpac':
          return record.name;
        case 'candidate':
          return record.full_name;
        case 'petition':
          return record.title;
        case 'news':
          return record.title;
        case 'user':
          return record.fullname;
        default:
          break;
      }
    },
    getRecordType(record) {
      switch (record.objectType) {
        case 'crowdpac':
          return 'Campaign';
        case 'candidate':
          return 'Candidate';
        case 'petition':
          return 'Petition';
        case 'news':
          return 'News';
        case 'user':
          return 'User';
        default:
          return '';
      }
    },
    getRecordLinkText(record) {
      switch (record.objectType) {
        case 'crowdpac':
          return 'View Campaign';
        case 'candidate':
          return record.race && record.race.length ? record.race : 'View Candidate';
        case 'petition':
          return 'View Petition';
        case 'news':
          return 'View Article';
        case 'user':
          return 'View Profile';
        default:
          return 'View';
      }
    },
    getRecordUrl(record) {
      switch (record.objectType) {
        case 'crowdpac':
          return record.permalink;
        default:
          return record.url;
      }
    },
  },
};
</script>

<template>
  <div class="container">
    <div v-if="error">
      {{ error }}
    </div>
    <div v-if="records.length">
      <div v-for="(record, index) in records" :key="record.objectID" class="box">
        <div class="record-container" :class="{ mobile: $root.isMobile, desktop: !$root.isMobile }">
          <div class="image-container">
            <a :href="getRecordUrl(record)"><img :src="record.image" class="image" /></a>
          </div>
          <div class="info">
            <a class="title" :href="getRecordUrl(record)">{{ getRecordTitle(record) }}</a>
            <div class="type">{{ getRecordType(record) }}</div>
          </div>
          <a class="link" :href="getRecordUrl(record)">{{ getRecordLinkText(record) }} </a>
        </div>
        <hr v-if="index != records.length - 1" />
      </div>
    </div>
    <transition name="feed-fade">
      <infinite-loading @infinite="fetch">
        <div slot="spinner">
          <div class="heading-4"><vue-simple-spinner v-margin="1" />Searching...</div>
        </div>
        <div slot="no-more">
          <div class="heading-1">
            <p>That's all!</p>
            <p />
          </div>
        </div>
        <div slot="no-results">
          <div class="heading-1">
            <p>This query returned no search results.</p>
            <p />
            <p>You can try again or return home.</p>
          </div>
        </div>
      </infinite-loading>
    </transition>
  </div>
</template>

<style scoped>
.container {
  display: flex;
  flex-direction: column;
}

.record-container {
  display: flex;
  align-items: center;
  padding-top: 12px;
  padding-bottom: 12px;
}

.record-container.mobile {
  flex-direction: column;
  text-align: center;
}

.record-container .info {
  flex: 1;
}

.record-container .info .title {
  font-size: 1.2rem;
  font-weight: bold;
  color: var(--highlight);
}

.record-container .info .type {
  font-size: 1rem;
  font-weight: normal;
  color: var(--muted);
}

.record-container .link {
  flex: 0;
}

.record-container.desktop .link {
  margin-left: 12px;
  min-width: 140px;
  text-align: right;
}

.image-container {
  flex: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.image {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  object-fit: cover;
  object-position: center;
}

.record-container.desktop .image-container {
  margin-right: 12px;
}
</style>
